import { useEffect } from 'react';
import { clsx } from 'clsx';
import { XMarkIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import type { ConflictModalProps } from './MasterDocument.types';
import { useMasterDocumentTranslation } from './i18n';

/**
 * Modal de alerta de conflicto de edición concurrente.
 *
 * Se muestra cuando otro usuario ha guardado cambios en el mismo documento
 * mientras el usuario actual estaba editando. Ofrece dos acciones: recargar
 * el documento (perder cambios locales) o seguir editando (mantener cambios).
 *
 * @see docs/colors.md - Sistema de tokens de color
 * @see MasterDocument.types - ConflictModalProps
 *
 * @example
 * ```tsx
 * <ConflictModal
 *   open={hasConflict}
 *   modifiedBy="Carlos Pérez"
 *   modifiedAgo="hace 2 minutos"
 *   onReload={() => reloadDocument()}
 *   onKeepEditing={() => dismissConflict()}
 * />
 * ```
 */
export function ConflictModal({
  open,
  modifiedBy,
  modifiedAgo,
  onReload,
  onKeepEditing,
  dismissable = true,
  message,
  className,
}: ConflictModalProps) {
  const { t } = useMasterDocumentTranslation();
  useEffect(() => {
    if (!open) return;
    function handleKey(e: KeyboardEvent) {
      // En modo duro (no descartable) Escape NO cierra: la única salida es recargar.
      if (e.key === 'Escape' && dismissable) onKeepEditing?.();
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open, onKeepEditing, dismissable]);

  if (!open) return null;

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 z-[300] bg-black/30 animate-[fadeIn_150ms_ease-out]"
        aria-hidden="true"
      />

      {/* Modal */}
      <div
        className={clsx(
          'fixed z-[301] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2',
          'w-[calc(100%-32px)] max-w-[440px]',
          'bg-bg-primary rounded-2xl px-6 py-5',
          'shadow-lg',
          'animate-[fadeIn_150ms_ease-out]',
          'dark:bg-dark-bg-primary dark:shadow-none dark:border dark:border-dark-border-primary',
          className,
        )}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="conflict-title"
        aria-describedby="conflict-desc"
      >
        {/* Close button — solo en modo descartable (blando). En el conflicto duro
            del servidor (409) la única acción permitida es recargar. */}
        {dismissable && (
          <button
            type="button"
            onClick={() => onKeepEditing?.()}
            className="absolute top-4 right-4 p-1 rounded-md hover:bg-background-secondary text-content-secondary transition-all duration-150 focus:outline-hidden focus:ring-2 focus:ring-primary-400 focus:ring-offset-2 dark:focus:ring-dark-border-custom dark:focus:ring-offset-dark-bg-primary dark:hover:bg-dark-bg-secondary"
            aria-label={t('master_document.chrome.close')}
          >
            <XMarkIcon className="w-4 h-4" aria-hidden="true" />
          </button>
        )}

        {/* Icon */}
        <div className="flex items-center justify-center w-12 h-12 rounded-full bg-amber-50 mb-4 dark:bg-amber-700/15">
          <ExclamationTriangleIcon className="w-6 h-6 text-amber-700 dark:text-amber-400" aria-hidden="true" />
        </div>

        {/* Title */}
        <h2
          id="conflict-title"
          className="text-base font-bold text-content-primary dark:text-dark-content-primary mb-1"
        >
          {t('master_document.document.conflictTitle')}
        </h2>

        {/* Subtitle */}
        {modifiedBy && (
          <p className="text-sm text-content-tertiary dark:text-dark-content-secondary mb-3">
            {t('master_document.document.conflictModifiedBy', {
              user: modifiedBy,
              when: modifiedAgo ?? t('master_document.document.conflictRecently'),
            })}
          </p>
        )}

        {/* Description */}
        {dismissable ? (
          <>
            <p
              id="conflict-desc"
              className="text-sm text-content-tertiary dark:text-dark-content-secondary mb-1"
            >
              {t('master_document.document.conflictReloadWarning')}
            </p>
            <p className="text-sm font-semibold text-content-primary dark:text-dark-content-primary mb-5">
              {t('master_document.document.conflictWhatToDo')}
            </p>
          </>
        ) : (
          <p
            id="conflict-desc"
            className="text-sm text-content-tertiary dark:text-dark-content-secondary mb-5"
          >
            {message ?? t('master_document.document.conflictBody')}
          </p>
        )}

        {/* Actions */}
        <div className="flex items-center justify-end gap-3">
          {dismissable && (
            <button
              type="button"
              onClick={() => onKeepEditing?.()}
              className={clsx(
                'px-4 py-2 rounded-md text-sm font-semibold',
                'border border-border-primary text-content-tertiary hover:bg-background-secondary transition-all duration-150',
                'focus:outline-hidden focus:ring-2 focus:ring-primary-400 focus:ring-offset-2',
                'dark:focus:ring-dark-border-custom dark:focus:ring-offset-dark-bg-primary',
                'dark:border-dark-border-primary dark:text-dark-content-secondary dark:hover:bg-dark-bg-secondary',
              )}
            >
              {t('master_document.document.conflictKeepChanges')}
            </button>
          )}
          <button
            type="button"
            onClick={onReload}
            className={clsx(
              'px-5 py-2 rounded-md text-sm font-semibold',
              'bg-primary-inverse-background text-white hover:bg-primary-700 transition-all duration-150',
              'dark:bg-dark-bg-inverse dark:text-dark-content-inverse dark:hover:bg-dark-bg-inverse/90',
              'focus:outline-hidden focus:ring-2 focus:ring-primary-400 focus:ring-offset-2',
              'dark:focus:ring-dark-border-custom dark:focus:ring-offset-dark-bg-primary',
            )}
          >
            {t('master_document.document.conflictReload')}
          </button>
        </div>
      </div>
    </>
  );
}
